import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from './BottomNav';
import {
  LayoutDashboard, FolderOpen, Award, BarChart2, Plus,
  ChevronRight, ShieldCheck, MailWarning, X, Medal, Kanban,
  HelpCircle, Settings, CreditCard, Bell, Briefcase,
  LogOut, ExternalLink, ChevronDown, Menu,
} from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';
import { cloudinaryAvatar } from '../utils/cloudinary';

/* ─── Logo ─────────────────────────────────────────────────── */
function SidebarLogo({ plan }) {
  const planLabel = plan === 'pro' ? 'Pro' : plan === 'agency' ? 'Agency' : null;
  return (
    <NavLink to="/dashboard" className="sb-logo">
      <div className="sb-logo-mark">
        <svg width="14" height="11" viewBox="0 0 14 11" fill="none">
          <path d="M1 5.5L5 9.5L13 1.5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <span className="sb-logo-text">VETT Work</span>
      {planLabel && <span className="sb-logo-badge">{planLabel}</span>}
    </NavLink>
  );
}

/* ─── Profile dropdown in topbar ───────────────────────────── */
function TopbarProfile({ user, logout }) {
  const [open, setOpen] = useState(false);
  const [avatarErr, setAvatarErr] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const initials = (user?.display_name || user?.email || 'U').slice(0, 2).toUpperCase();

  useEffect(() => { setAvatarErr(false); }, [user?.avatar_url]);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="tb-profile"
        onClick={() => setOpen(o => !o)}
        data-testid="avatar-btn"
      >
        <div className="tb-av">
          {user?.avatar_url && !avatarErr
            ? <img src={cloudinaryAvatar(user.avatar_url, 64)} alt="" onError={() => setAvatarErr(true)} />
            : initials}
        </div>
        <span className="tb-profile-name">{user?.display_name?.split(' ')[0] || 'Account'}</span>
        <ChevronDown size={13} color="var(--ink3)" />
      </button>
      {open && (
        <div className="dropdown-panel" style={{ minWidth: 200 }}>
          <div className="dropdown-item" onClick={() => { window.open(`/u/${user?.slug}`, '_blank'); setOpen(false); }}>
            <ExternalLink size={14} /> View public profile
          </div>
          <div className="dropdown-item" onClick={() => { navigate('/dashboard/profile'); setOpen(false); }}>
            <Award size={14} /> Profile settings
          </div>
          <div className="dropdown-item" onClick={() => { navigate('/dashboard/settings'); setOpen(false); }}>
            <Settings size={14} /> Account settings
          </div>
          <div className="dropdown-divider" />
          <div className="dropdown-item danger" data-testid="sign-out-btn" onClick={() => { logout(); navigate('/login'); setOpen(false); }}>
            <LogOut size={14} /> Sign out
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Notif bell ────────────────────────────────────────────── */
function NotifBell({ count, announcements, onDismissAnnouncement }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const ref = useRef(null);
  const annNotifs = (announcements || []).filter(a => a.type === 'notification' && !a.dismissed);
  const totalBadge = count + annNotifs.length;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadNotifs = async () => {
    try { const res = await api.get('/user/notifications'); setNotifs(res.data.slice(0, 5)); } catch {}
  };

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button
        className="tb-notif"
        onClick={() => { if (!open) loadNotifs(); setOpen(o => !o); }}
        data-testid="notif-bell"
      >
        <Bell size={16} color="var(--ink3)" />
        {totalBadge > 0 && <div className="tb-notif-dot" />}
        {totalBadge > 0 && <span className="notif-badge" data-testid="notif-count">{totalBadge > 9 ? '9+' : totalBadge}</span>}
      </button>
      {open && (
        <div className="dropdown-panel" style={{ width: 320 }}>
          <div style={{ padding: '4px 10px 6px', fontSize: '.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'rgba(255,255,255,.3)', fontFamily: 'var(--mono)' }}>
            Notifications
          </div>
          {annNotifs.map(a => (
            <div key={a.id} className="dropdown-item" style={{ fontSize: '.8rem', alignItems: 'flex-start', gap: '.6rem' }}>
              <span style={{ color: '#F59E0B', flexShrink: 0, marginTop: 2 }}>•</span>
              <span style={{ flex: 1 }}>
                <strong style={{ color: '#fff', display: 'block', marginBottom: 2 }}>{a.title}</strong>
                <span style={{ color: 'rgba(255,255,255,.55)' }}>{a.message}</span>
              </span>
              <button onClick={() => onDismissAnnouncement(a.id)} data-testid={`dismiss-ann-notif-${a.id}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,.3)', padding: '0 2px', flexShrink: 0 }}>×</button>
            </div>
          ))}
          {notifs.length === 0 && annNotifs.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: '.8rem', color: 'rgba(255,255,255,.3)' }}>No notifications</div>
          ) : notifs.map((n, i) => (
            <div key={i} className="dropdown-item" style={{ opacity: n.read ? .55 : 1, fontSize: '.8rem' }}>
              <span style={{ color: 'var(--blue3)', flexShrink: 0 }}>•</span>
              <span style={{ flex: 1 }}>{n.message}</span>
            </div>
          ))}
          <div className="dropdown-divider" />
          <div className="dropdown-item" style={{ justifyContent: 'center', fontSize: '.78rem' }}
            onClick={() => { navigate('/dashboard/notifications'); setOpen(false); }}>
            View all <ChevronRight size={13} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Page title map ────────────────────────────────────────── */
const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/dashboard/projects': 'My Projects',
  '/dashboard/projects/new': 'New Project',
  '/dashboard/profile': 'Profile',
  '/dashboard/billing': 'Billing',
  '/dashboard/pricing': 'Pricing',
  '/dashboard/settings': 'Settings',
  '/dashboard/notifications': 'Notifications',
  '/dashboard/badge': 'Badge & Embed',
  '/dashboard/badges': 'My Badges',
  '/dashboard/analytics': 'Analytics',
  '/dashboard/pipeline': 'Pipeline',
  '/dashboard/services': 'Services',
  '/dashboard/support': 'Support',
  '/dashboard/agency': 'Agency',
};

/* ─── Main Layout ───────────────────────────────────────────── */
export default function Layout() {
  const { user, logout, refreshUser, isImpersonating, stopImpersonation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [unreadCount, setUnreadCount] = useState(0);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedBanners, setDismissedBanners] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem('dismissed_banners') || '[]')); }
    catch { return new Set(); }
  });

  const fetchAnnouncements = useCallback(async () => {
    if (!user) return;
    try { const res = await api.get('/announcements/active'); setAnnouncements(res.data || []); } catch {}
  }, [user]);

  useEffect(() => {
    fetchAnnouncements();
    const iv = setInterval(fetchAnnouncements, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [fetchAnnouncements]);

  useEffect(() => {
    api.get('/user/notifications').then(res => {
      setUnreadCount(res.data.filter(n => !n.read).length);
    }).catch(() => {});
  }, [location.pathname]);

  const dismissBanner = (id) => {
    const next = new Set([...dismissedBanners, id]);
    setDismissedBanners(next);
    sessionStorage.setItem('dismissed_banners', JSON.stringify([...next]));
  };

  const dismissNotification = async (id) => {
    try {
      await api.post(`/announcements/${id}/dismiss`);
      setAnnouncements(a => a.map(x => x.id === id ? { ...x, dismissed: true } : x));
    } catch {}
  };

  const activeBanners = announcements.filter(a => a.type === 'banner' && !dismissedBanners.has(a.id));

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await api.post('/auth/resend-verification');
      toast.success('Verification email sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email.');
    } finally { setResendLoading(false); }
  };

  const slug = user?.slug || '';
  const initials = (user?.display_name || user?.email || 'U').slice(0, 2).toUpperCase();
  const showVerifyBanner = user && user.email_verified === false && !bannerDismissed;

  const pageTitle = PAGE_TITLES[location.pathname] ||
    (location.pathname.startsWith('/dashboard/projects/') ? 'Project Detail' : 'Dashboard');

  /* ── Nav sections ─────────────────────────────────────────── */
  const mainNav = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { to: '/dashboard/projects', label: 'My Projects', icon: FolderOpen },
    { to: '/dashboard/analytics', label: 'Analytics', icon: BarChart2 },
    { to: '/dashboard/badges', label: 'My Badges', icon: Medal },
    { to: '/dashboard/services', label: 'Services', icon: Briefcase },
  ];
  const workNav = [
    { to: '/dashboard/pipeline', label: 'Pipeline', icon: Kanban },
  ];
  const accountNav = [
    { to: '/dashboard/billing', label: 'Billing', icon: CreditCard },
    { to: '/dashboard/settings', label: 'Settings', icon: Settings },
    { to: '/dashboard/notifications', label: 'Notifications', icon: Bell },
  ];

  function SbLink({ to, label, icon: Icon, exact }) {
    return (
      <NavLink
        to={to}
        end={exact}
        className={({ isActive }) => `sb-item${isActive ? ' active' : ''}`}
        onClick={() => setMobileOpen(false)}
        data-testid={`nav-${label.toLowerCase().replace(/[& ]+/g, '-')}`}
      >
        <Icon size={16} strokeWidth={1.6} />
        {label}
      </NavLink>
    );
  }

  return (
    <>
      {/* Admin impersonation banner */}
      {isImpersonating && (
        <div data-testid="impersonation-banner" style={{
          position: 'fixed', top: 0, left: 0, right: 0, height: 44, zIndex: 1000,
          background: '#06070F', borderBottom: '1px solid rgba(245,158,11,.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 1.5rem',
        }}>
          <span style={{ fontSize: '.8rem', color: '#F59E0B', fontWeight: 500 }}>
            Viewing as <strong style={{ fontFamily: 'var(--mono)' }}>{user?.display_name || user?.email}</strong>
          </span>
          <button onClick={() => { stopImpersonation(); window.location.replace('/admin/users'); }}
            data-testid="exit-impersonation-btn"
            style={{ fontSize: '.75rem', fontWeight: 700, color: '#06070F', background: '#F59E0B', border: 'none', borderRadius: 6, padding: '.3rem .85rem', cursor: 'pointer' }}>
            Exit to Admin Panel
          </button>
        </div>
      )}

      {/* App Grid */}
      <div className="app" style={isImpersonating ? { marginTop: 44, height: 'calc(100vh - 44px)' } : {}}>

        {/* Mobile overlay */}
        {mobileOpen && (
          <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
        )}

        {/* ── Sidebar ───────────────────────────────────────────── */}
        <aside className={`sidebar${mobileOpen ? ' open' : ''}`} data-testid="sidebar" role="navigation">
          <SidebarLogo plan={user?.plan} />

          {/* New Project CTA */}
          <button
            className="sb-item"
            style={{ margin: '4px 8px 6px', background: 'rgba(34,96,196,.2)', color: '#fff', border: '1px solid rgba(34,96,196,.3)', fontWeight: 600 }}
            onClick={() => { navigate('/dashboard/projects/new'); setMobileOpen(false); }}
            data-testid="sidebar-new-project-btn"
          >
            <Plus size={15} strokeWidth={2.5} /> New Project
          </button>

          {/* MAIN section */}
          <div className="sb-section">Main</div>
          {mainNav.map(n => <SbLink key={n.to} {...n} />)}

          {/* WORK section */}
          <div className="sb-section">Work</div>
          {workNav.map(n => <SbLink key={n.to} {...n} />)}

          {/* ACCOUNT section */}
          <div className="sb-section">Account</div>
          {accountNav.map(n => <SbLink key={n.to} {...n} />)}

          {/* Support */}
          <div className="sb-section">Support</div>
          <NavLink
            to="/dashboard/support"
            className={({ isActive }) => `sb-item${isActive ? ' active' : ''}`}
            onClick={() => setMobileOpen(false)}
            data-testid="nav-support"
          >
            <HelpCircle size={16} strokeWidth={1.6} /> Support
          </NavLink>

          {/* Admin */}
          {(user?.is_admin || user?.role === 'manager') && (
            <>
              <div className="sb-section">Admin</div>
              <NavLink to="/admin" className={({ isActive }) => `sb-item${isActive ? ' active' : ''}`}
                onClick={() => setMobileOpen(false)} data-testid="nav-admin">
                <ShieldCheck size={16} strokeWidth={1.6} /> Admin Panel
                <span className="sb-badge">{user?.is_admin ? 'A' : 'M'}</span>
              </NavLink>
            </>
          )}

          {/* Footer: user info */}
          <div className="sb-footer">
            <div className="sb-user" data-testid="sidebar-user" onClick={() => navigate('/dashboard/profile')}>
              <div className="sb-avatar">
                {user?.avatar_url
                  ? <img src={cloudinaryAvatar(user.avatar_url, 64)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} onError={e => { e.currentTarget.style.display = 'none'; }} />
                  : null}
                <span style={{ display: user?.avatar_url ? 'none' : 'flex' }}>{initials}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="sb-user-name">{user?.display_name || 'User'}</div>
                <div className="sb-user-plan">{user?.plan === 'pro' ? 'Pro' : user?.plan === 'agency' ? 'Agency' : 'Free'}{slug ? ` · getvett.org/u/${slug}` : ''}</div>
              </div>
            </div>
          </div>
        </aside>

        {/* ── Topbar ────────────────────────────────────────────── */}
        <header className="topbar">
          <div className="tb-left">
            <button className="mob-menu-btn" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
              <Menu size={18} />
            </button>
            <span className="tb-breadcrumb" data-testid="page-title">{pageTitle}</span>
          </div>
          <div className="tb-right">
            {/* New Project button */}
            <button className="tb-btn tb-btn-primary" onClick={() => navigate('/dashboard/projects/new')} data-testid="topnav-new-project">
              <Plus size={14} strokeWidth={2.5} /> New Project
            </button>
            {/* Notifications */}
            <NotifBell count={unreadCount} announcements={announcements} onDismissAnnouncement={dismissNotification} />
            {/* Profile dropdown */}
            <TopbarProfile user={user} logout={logout} />
          </div>
        </header>

        {/* ── Main content column ───────────────────────────────── */}
        <div className="main-wrapper" style={{ gridColumn: 2, gridRow: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg)' }}>
          {/* Email verify banner */}
          {showVerifyBanner && (
            <div className="verify-banner" data-testid="email-verify-banner">
              <MailWarning size={15} color="var(--amber)" style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: '.82rem', color: 'var(--amber)', minWidth: 200 }}>
                Please verify your email. Check <strong>{user?.email}</strong> for a verification link.
              </span>
              <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
                <button onClick={handleResendVerification} disabled={resendLoading} data-testid="resend-verify-btn"
                  style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--amber)', background: 'rgba(183,119,13,.12)', border: '1px solid rgba(183,119,13,.3)', borderRadius: 6, padding: '.3rem .75rem', cursor: 'pointer' }}>
                  {resendLoading ? 'Sending...' : 'Resend'}
                </button>
                <button onClick={() => setBannerDismissed(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(183,119,13,.6)', display: 'flex', padding: 4 }}>
                  <X size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Announcement banners */}
          {activeBanners.map(banner => (
            <div key={banner.id} className="announce-bar" data-testid={`announcement-banner-${banner.id}`}>
              <span><strong style={{ marginRight: '.35em' }}>{banner.title}:</strong>{banner.message}</span>
              <button onClick={() => dismissBanner(banner.id)} data-testid={`dismiss-banner-${banner.id}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400E', opacity: .6, flexShrink: 0, padding: 4, display: 'flex' }}>
                <X size={16} />
              </button>
            </div>
          ))}

          {/* Page content */}
          <div className="main" id="main-content">
            <Outlet />
          </div>
        </div>

        {/* Mobile bottom nav */}
        <BottomNav />
      </div>
    </>
  );
}
